import { z } from 'zod';
import type { TFunction } from 'i18next';

/**
 * One intraday work segment. `startTime`/`endTime` are plain `HH:mm` strings
 * (from `TimeSelect`), validated as non-empty with `endTime` strictly after
 * `startTime` — the backend has no dedicated error code for this, so the
 * client-side check is the only guard against an inverted range.
 *
 * @remarks
 * `order` is not part of the form — it's derived from the segment's position
 * in the array when building the API payload, so the user never edits it.
 */
const workSegmentSchema = (t: TFunction) =>
  z
    .object({
      startTime: z.string().min(1, { error: t('workSchedules:errors.segmentTimeRequired') }),
      endTime: z.string().min(1, { error: t('workSchedules:errors.segmentTimeRequired') }),
    })
    .refine((segment) => segment.endTime > segment.startTime, {
      error: t('workSchedules:errors.segmentEndBeforeStart'),
      path: ['endTime'],
    });

/**
 * `order` is derived from array position, so segments must already be sorted
 * chronologically with no overlap — a segment's end may equal the next one's
 * start (back-to-back), but never come after it.
 */
function isNonOverlappingAndSorted(segments: { startTime: string; endTime: string }[]): boolean {
  for (let i = 0; i < segments.length - 1; i++) {
    if (segments[i].endTime > segments[i + 1].startTime) return false;
  }
  return true;
}

/**
 * Tenant work schedule form schema.
 *
 * @remarks
 * `hoursPerDay` is no longer part of this form — it's derived server-side
 * from `segments` and shown read-only in the UI. `workingDays` still uses
 * `z.coerce.number()` because `FormFieldInput` stores values as strings.
 */
export const createTenantWorkScheduleSchema = (t: TFunction) =>
  z
    .object({
      segments: z
        .array(workSegmentSchema(t))
        .min(1, { error: t('workSchedules:errors.segmentsEmpty') }),
      workingDays: z.coerce
        .number({ error: t('workSchedules:errors.daysRequired') })
        .int({ error: t('workSchedules:errors.daysInt') })
        .min(1, { error: t('workSchedules:errors.daysRange') })
        .max(7, { error: t('workSchedules:errors.daysRange') }),
    })
    .refine(({ segments }) => isNonOverlappingAndSorted(segments), {
      error: t('workSchedules:errors.segmentsOverlap'),
      path: ['segments'],
    });

/** Raw form input (string fields before coercion) — drives `useForm`. */
export type TenantWorkScheduleFormInput = z.input<
  ReturnType<typeof createTenantWorkScheduleSchema>
>;
/** Validated, coerced output — received by the submit handler. */
export type TenantWorkScheduleFormValues = z.output<
  ReturnType<typeof createTenantWorkScheduleSchema>
>;

/** Per-member override form schema — segments only, `workingDays` stays at the tenant level. */
export const createMemberWorkScheduleSchema = (t: TFunction) =>
  z
    .object({
      segments: z
        .array(workSegmentSchema(t))
        .min(1, { error: t('workSchedules:errors.segmentsEmpty') }),
    })
    .refine(({ segments }) => isNonOverlappingAndSorted(segments), {
      error: t('workSchedules:errors.segmentsOverlap'),
      path: ['segments'],
    });

/** Raw form input (string fields before coercion) — drives `useForm`. */
export type MemberWorkScheduleFormInput = z.input<
  ReturnType<typeof createMemberWorkScheduleSchema>
>;
/** Validated, coerced output — received by the submit handler. */
export type MemberWorkScheduleFormValues = z.output<
  ReturnType<typeof createMemberWorkScheduleSchema>
>;
